import {
  DEFAULT_DOMAIN,
  getDomain,
  getEnabledFields,
  SEARCH_DOMAINS,
  withDynamicFilters,
} from "./searchDomains";
import { buildCatalogParam, CATALOGUE_PREFIX } from "./useCatalogueFilters";

/**
 * Contrato de la búsqueda: la URL es la única fuente de verdad.
 *
 * /search?type=investigations&q=ramirez&in=alias,title&status=PROCESS&page=2
 *
 * Así el link se puede compartir, recargar la página no pierde la búsqueda y el
 * botón "atrás" navega entre búsquedas, igual que en Google.
 */

export const DEFAULT_LIMIT = 10;

/** URLSearchParams -> criterio de búsqueda, ya validado contra el dominio */
export const parseSearchCriteria = (searchParams, dynamicFilters = []) => {
  const type = SEARCH_DOMAINS[searchParams.get("type")]
    ? searchParams.get("type")
    : DEFAULT_DOMAIN;
  const domain = withDynamicFilters(getDomain(type), dynamicFilters);
  const enabledFields = getEnabledFields(domain);

  // Un campo de otro dominio (o uno deshabilitado) en la URL se descarta:
  // el backend lo rechazaría y la opción no existiría en la UI.
  let fields = (searchParams.get("in") || "")
    .split(",")
    .filter((field) => enabledFields.includes(field));

  // En los dominios de campo único una URL con varios se queda con el primero
  if (domain.singleField) fields = fields.slice(0, 1);

  // Los filtros multi-valor viajan como lista separada por comas
  // (status=new,pending) y se manejan como arreglo dentro de la app.
  const advanced = {};

  // Los filtros de catálogo se reconocen por el prefijo, no por la definición:
  // la URL debe sobrevivir aunque los catálogos aún no se hayan cargado.
  searchParams.forEach((value, key) => {
    if (!key.startsWith(CATALOGUE_PREFIX)) return;
    const values = value.split(",").filter(Boolean);
    if (values.length > 0) advanced[key] = values;
  });

  domain.filters.forEach(({ key, multiple }) => {
    if (key.startsWith(CATALOGUE_PREFIX)) return;
    const value = searchParams.get(key);
    if (!value) return;

    if (!multiple) {
      advanced[key] = value;
      return;
    }

    // Una URL malformada ("status=,") no debe dejar un arreglo vacío: en JS es
    // truthy y haría creer que hay filtro activo cuando no se manda ninguno.
    const values = value.split(",").filter(Boolean);
    if (values.length > 0) advanced[key] = values;
  });

  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || DEFAULT_LIMIT;

  return {
    type,
    domain,
    q: searchParams.get("q") || "",
    fields: fields.length > 0 ? fields : domain.defaultFields,
    advanced,
    page: page > 0 ? page : 1,
    limit: limit > 0 ? limit : DEFAULT_LIMIT,
  };
};

/** Criterio de búsqueda -> objeto plano para setSearchParams */
export const buildSearchParams = ({ type, q, fields, advanced, page, limit }) => {
  const params = {};

  if (type && type !== DEFAULT_DOMAIN) params.type = type;
  if (q) params.q = q;
  if (fields?.length > 0) params.in = fields.join(",");

  Object.entries(advanced || {}).forEach(([key, value]) => {
    const serialized = Array.isArray(value) ? value.join(",") : value;
    if (serialized) params[key] = String(serialized);
  });

  if (page > 1) params.page = String(page);
  if (limit !== DEFAULT_LIMIT) params.limit = String(limit);

  return params;
};

/** Criterio de búsqueda -> query params del endpoint del dominio */
export const buildRequestPayload = ({ q, fields, advanced, page, limit }) => {
  const payload = { offset: (page - 1) * limit, limit };

  // El endpoint recibe el mismo texto en cada campo elegido y los combina con OR.
  // Sin término no se manda ningún campo: los serializers de DRF rechazan un
  // CharField vacío con 400, y una búsqueda solo por filtros es válida.
  if (q.trim()) {
    fields.forEach((field) => {
      payload[field] = q;
    });
  }

  // Todas las selecciones de catálogo viajan juntas en un solo parámetro
  const catalogParam = buildCatalogParam(advanced);
  if (catalogParam) payload.catalog = catalogParam;

  Object.entries(advanced || {}).forEach(([key, value]) => {
    const serialized = Array.isArray(value) ? value.join(",") : value;
    if (!serialized) return;

    // Ya se colapsaron arriba en `catalog`
    if (key.startsWith(CATALOGUE_PREFIX)) return;

    if (key === "indicators") {
      // Los indicadores viajan compactos en la URL pero el backend los espera
      // como indicator_<key>=true, la convencion que ya usa /report/list/.
      value.forEach((indicator) => {
        payload[`indicator_${indicator}`] = true;
      });
      return;
    }

    payload[key] = serialized;
  });

  return payload;
};

/**
 * Sin texto ni filtros no se consulta nada: los endpoints interpretan la
 * condición vacía como "tráete todo", que no es lo que espera quien abre el
 * buscador.
 */
export const hasActiveSearch = ({ q, advanced }) =>
  q.trim().length > 0 || Object.values(advanced || {}).some(Boolean);

/**
 * Al cambiar de dominio no sobreviven ni los campos ni los filtros del
 * anterior: pertenecen a otro endpoint.
 */
export const switchDomain = (criteria, type) => ({
  ...criteria,
  type,
  fields: getDomain(type).defaultFields,
  advanced: {},
  page: 1,
});

/** Alterna un valor dentro de un filtro multi-valor */
export const toggleFilterValue = (advanced = {}, key, value) => {
  const current = advanced[key] || [];
  const next = current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];

  const updated = { ...advanced };
  if (next.length > 0) updated[key] = next;
  else delete updated[key];

  return updated;
};
